const BASIS_THEORY_ORIGIN = 'https://js.basistheory.com';

const BASIS_THEORY_CARD_FRAME_PATHS = new Set([
  '/hosted-elements/card-number-element.html',
  '/hosted-elements/card-expiration-date-element.html',
  '/hosted-elements/card-verification-code-element.html',
]);

export const BASIS_THEORY_CARD_AUTOFILL_ALLOW = 'clipboard-write; autofill *';

const isBasisTheoryCardFrame = (frame: HTMLIFrameElement): boolean => {
  try {
    const url = new URL(frame.src, window.location.href);
    return (
      url.origin === BASIS_THEORY_ORIGIN &&
      url.pathname.includes('/web-elements/') &&
      Array.from(BASIS_THEORY_CARD_FRAME_PATHS).some((path) => url.pathname.endsWith(path))
    );
  } catch {
    return false;
  }
};

/**
 * Delegate browser saved-card autofill only to Basis Theory's three reviewed
 * secure card frames. Existing permissions are preserved because Basis Theory
 * also requires clipboard-write for its input implementation.
 */
export const enableBasisTheoryCardAutofill = (frame: HTMLIFrameElement): boolean => {
  if (!isBasisTheoryCardFrame(frame)) return false;

  const directives = new Set(
    frame.allow
      .split(';')
      .map((directive) => directive.trim())
      .filter(Boolean),
  );
  directives.add('clipboard-write');
  directives.add('autofill *');
  frame.allow = Array.from(directives).join('; ');
  return true;
};

/**
 * Basis Theory appends its iframe asynchronously during mount. Observe only
 * the SDK-owned provider target and return an idempotent lifecycle cleanup.
 */
export const installBasisTheoryCardAutofill = (
  root: ParentNode,
  onCardFrame?: (frame: HTMLIFrameElement) => void,
): (() => void) => {
  const applyToTree = (node: Node): void => {
    if (node instanceof HTMLIFrameElement && enableBasisTheoryCardAutofill(node)) {
      onCardFrame?.(node);
    }
    if (!(node instanceof Element || node instanceof DocumentFragment)) return;
    node.querySelectorAll('iframe').forEach((frame) => {
      if (enableBasisTheoryCardAutofill(frame)) onCardFrame?.(frame);
    });
  };

  applyToTree(root as Node);

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'attributes') {
        applyToTree(mutation.target);
        continue;
      }
      mutation.addedNodes.forEach(applyToTree);
    }
  });
  observer.observe(root, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['src'],
  });

  let installed = true;
  return () => {
    if (!installed) return;
    installed = false;
    observer.disconnect();
  };
};
